export type ModelStatus = 'not_loaded' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface ModelProgress {
  phase: 'idle' | 'checking' | 'downloading' | 'verifying' | 'ready' | 'error';
  progressFraction: number;
  downloadedBytes: number;
  totalBytes: number;
  currentFile: string;
  error?: string;
}

export interface ManifestFile {
  path: string;
  url: string;
  size: number;
  sha256: string;
}

export interface ModelManifest {
  id: string;
  version: string;
  name: string;
  license: string;
  attribution: string;
  totalSizeBytes: number;
  files: ManifestFile[];
}

export interface CapabilityReport {
  isSupported: boolean;
  secureContext: boolean;
  hasGetUserMedia: boolean;
  hasAudioWorklet: boolean;
  hasWorker: boolean;
  hasWebAssembly: boolean;
  hasCaches: boolean;
  failureReason?: string;
}

const MODEL_CACHE_NAME = 'tp_speech_models_v1';

export class ModelManager {
  private status: ModelStatus = 'not_loaded';
  private manifest: ModelManifest | null = null;
  private abortController: AbortController | null = null;

  getStatus(): ModelStatus {
    return this.status;
  }

  /**
   * Evaluates browser platform capabilities for local Voice Follow
   */
  checkCapabilities(): CapabilityReport {
    if (typeof window === 'undefined') {
      return {
        isSupported: false,
        secureContext: false,
        hasGetUserMedia: false,
        hasAudioWorklet: false,
        hasWorker: false,
        hasWebAssembly: false,
        hasCaches: false,
        failureReason: 'Running in non-browser environment',
      };
    }

    const secureContext = window.isSecureContext ?? false;
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasAudioWorklet = typeof AudioWorkletNode !== 'undefined';
    const hasWorker = typeof Worker !== 'undefined';
    const hasWebAssembly = typeof WebAssembly !== 'undefined';
    const hasCaches = 'caches' in window;

    if (!secureContext) {
      return {
        isSupported: false,
        secureContext,
        hasGetUserMedia,
        hasAudioWorklet,
        hasWorker,
        hasWebAssembly,
        hasCaches,
        failureReason: 'Microphone requires HTTPS or localhost (insecure context)',
      };
    }

    if (!hasGetUserMedia) {
      return {
        isSupported: false,
        secureContext,
        hasGetUserMedia,
        hasAudioWorklet,
        hasWorker,
        hasWebAssembly,
        hasCaches,
        failureReason: 'Microphone access (getUserMedia) is not supported in this browser',
      };
    }

    if (!hasAudioWorklet) {
      return {
        isSupported: false,
        secureContext,
        hasGetUserMedia,
        hasAudioWorklet,
        hasWorker,
        hasWebAssembly,
        hasCaches,
        failureReason: 'AudioWorklet is required for 16kHz audio capture',
      };
    }

    if (!hasWebAssembly || !hasWorker) {
      return {
        isSupported: false,
        secureContext,
        hasGetUserMedia,
        hasAudioWorklet,
        hasWorker,
        hasWebAssembly,
        hasCaches,
        failureReason: 'WebAssembly and Web Workers are required for local speech inference',
      };
    }

    return {
      isSupported: true,
      secureContext,
      hasGetUserMedia,
      hasAudioWorklet,
      hasWorker,
      hasWebAssembly,
      hasCaches,
    };
  }

  /**
   * Checks if model files are already cached locally
   */
  async isModelCached(): Promise<boolean> {
    try {
      if (!('caches' in window)) return false;
      const cache = await caches.open(MODEL_CACHE_NAME);
      const manifest = await this.getManifest();
      if (!manifest) return false;

      for (const f of manifest.files) {
        const reqUrl = `/models/${manifest.id}/${manifest.version}/${f.path}`;
        const match = await cache.match(reqUrl);
        if (!match) return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Fetches the model manifest
   */
  async getManifest(): Promise<ModelManifest | null> {
    if (this.manifest) return this.manifest;
    try {
      const res = await fetch('/models/manifest.json');
      if (!res.ok) return null;
      this.manifest = await res.json();
      return this.manifest;
    } catch {
      return null;
    }
  }

  /**
   * Downloads model files same-origin, verifies SHA-256 hashes, and stores in dedicated Cache Storage
   */
  async downloadAndVerify(
    onProgress: (prog: ModelProgress) => void
  ): Promise<boolean> {
    const caps = this.checkCapabilities();
    if (!caps.isSupported) {
      this.status = 'unavailable';
      onProgress({
        phase: 'error',
        progressFraction: 0,
        downloadedBytes: 0,
        totalBytes: 0,
        currentFile: '',
        error: caps.failureReason,
      });
      return false;
    }

    const manifest = await this.getManifest();
    if (!manifest) {
      this.status = 'error';
      onProgress({
        phase: 'error',
        progressFraction: 0,
        downloadedBytes: 0,
        totalBytes: 0,
        currentFile: '',
        error: 'Speech model manifest not found',
      });
      return false;
    }

    this.status = 'loading';
    this.abortController = new AbortController();

    const cache = await caches.open(MODEL_CACHE_NAME);
    let totalDownloaded = 0;
    const totalBytes = manifest.totalSizeBytes;

    try {
      for (const file of manifest.files) {
        const fileUrl = `/models/${manifest.id}/${manifest.version}/${file.path}`;

        onProgress({
          phase: 'downloading',
          progressFraction: totalDownloaded / totalBytes,
          downloadedBytes: totalDownloaded,
          totalBytes,
          currentFile: file.path,
        });

        // Fetch file
        const res = await fetch(fileUrl, { signal: this.abortController.signal });
        if (!res.ok) {
          throw new Error(`Failed to download ${file.path}: HTTP ${res.status}`);
        }

        const buffer = await res.arrayBuffer();

        // Verify SHA-256
        onProgress({
          phase: 'verifying',
          progressFraction: totalDownloaded / totalBytes,
          downloadedBytes: totalDownloaded,
          totalBytes,
          currentFile: file.path,
        });

        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

        if (hashHex.toLowerCase() !== file.sha256.toLowerCase()) {
          throw new Error(`SHA-256 mismatch for ${file.path}. Expected ${file.sha256}, got ${hashHex}`);
        }

        // Store into dedicated cache
        const cacheResponse = new Response(buffer, {
          headers: {
            'Content-Type': 'application/octet-stream',
            'Cache-Control': 'public, max-age=31536000, immutable',
          },
        });
        await cache.put(fileUrl, cacheResponse);

        totalDownloaded += file.size;
      }

      this.status = 'ready';
      onProgress({
        phase: 'ready',
        progressFraction: 1.0,
        downloadedBytes: totalBytes,
        totalBytes,
        currentFile: '',
      });
      return true;
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        this.status = 'not_loaded';
        onProgress({
          phase: 'idle',
          progressFraction: 0,
          downloadedBytes: 0,
          totalBytes: 0,
          currentFile: '',
        });
        return false;
      }

      this.status = 'error';
      onProgress({
        phase: 'error',
        progressFraction: 0,
        downloadedBytes: totalDownloaded,
        totalBytes,
        currentFile: '',
        error: err?.message || 'Model download failed',
      });
      return false;
    } finally {
      this.abortController = null;
    }
  }

  cancelDownload(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.status = 'not_loaded';
  }

  async removeModel(): Promise<void> {
    try {
      if ('caches' in window) {
        await caches.delete(MODEL_CACHE_NAME);
      }
      this.status = 'not_loaded';
    } catch {
      // ignore
    }
  }
}
